import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { RefreshCw, TrendingUp, Users, Calendar, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Analytics = () => {
  const [gyms, setGyms] = useState([]);
  const [selectedGymId, setSelectedGymId] = useState('');
  const [drilldown, setDrilldown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchGyms = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/gyms?perPage=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setGyms(data.data);
        if (data.data.length > 0) {
          setSelectedGymId(data.data[0].id);
        }
      }
    } catch (err) {
      // Offline fallback
      setGyms([
        { id: '1', name: 'Muscle Factory, Hyd' },
        { id: '2', name: 'Iron House, Vijayawada' },
        { id: '4', name: 'Gold Gym, Vizag' }
      ]);
      setSelectedGymId('1');
    }
  };

  const fetchDrilldown = async (gymId) => {
    if (!gymId) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/analytics/${gymId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDrilldown(data.data);
      }
    } catch (err) {
      setError('Showing simulation data.');
      setDrilldown({
        gymName: gyms.find(g => g.id === gymId)?.name || 'Muscle Factory, Hyd',
        checkInChart: [
          { date: 'Mon', count: 12 }, { date: 'Tue', count: 18 }, { date: 'Wed', count: 24 },
          { date: 'Thu', count: 15 }, { date: 'Fri', count: 32 }, { date: 'Sat', count: 45 }, { date: 'Sun', count: 38 }
        ],
        retention: {
          totalMembers: 145,
          activeLast7Days: 85,
          activeLast30Days: 130,
          retentionRate7: 58.6,
          retentionRate30: 89.7
        },
        challengeStats: {
          totalCheckins: 1050,
          activeStreaks: 14.5
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGyms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedGymId) {
      fetchDrilldown(selectedGymId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGymId]);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Analytics Hub</h1>
          <p className="text-gray-400 text-sm mt-1">Isolate specific gym workspaces to monitor member retention and engagement trendlines.</p>
        </div>

        {/* Selector */}
        <div className="flex items-center space-x-2 bg-[#0F172A] border border-[#1E293B] px-3 py-2 rounded-lg">
          <span className="text-xs text-gray-400">Select Gym:</span>
          <select
            value={selectedGymId}
            onChange={(e) => setSelectedGymId(e.target.value)}
            className="bg-transparent border-none text-xs text-[#6CD800] font-bold focus:outline-none focus:ring-0 cursor-pointer"
          >
            {gyms.map(g => (
              <option key={g.id} value={g.id} className="bg-[#0F172A] text-white">{g.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64 text-[#6CD800]">
          <RefreshCw size={24} className="animate-spin mr-2" />
          Compiling trend analyses...
        </div>
      ) : drilldown ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Chart Card */}
          <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg lg:col-span-2 overflow-hidden">
            <h3 className="text-lg font-bold text-white mb-6">Check-in Scan Trendlines</h3>
            {/* simple visual chart */}
            <div className="overflow-x-auto w-full">
              <div className="h-64 flex items-end justify-between gap-1 sm:gap-2 border-b border-[#1E293B] pb-4 pt-8 min-w-[500px] lg:min-w-0">
                {drilldown.checkInChart.map((d, idx) => {
                  const maxCount = Math.max(...drilldown.checkInChart.map(x => x.count), 1);
                  const heightPercent = `${(d.count / maxCount) * 80 + 10}%`;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center group h-full justify-end">
                      <div className="text-[10px] text-gray-500 font-bold mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {d.count}
                      </div>
                      <div
                        style={{ height: heightPercent }}
                        className="w-full bg-[#6CD800]/20 hover:bg-[#6CD800] border border-[#6CD800]/30 rounded-t transition-all cursor-pointer duration-300"
                      />
                      <div className="text-[10px] text-gray-400 font-medium mt-2 truncate w-full text-center min-h-[16px]">
                        {formatChartDate(d.date, idx, drilldown.checkInChart.length)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-gray-400 italic mt-4 text-center">Daily scanning check-ins volume.</p>
          </div>

          {/* Age Distribution Card */}
          <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg lg:col-span-2 overflow-hidden">
            <h3 className="text-lg font-bold text-white mb-6">Member Age Distribution</h3>
            {drilldown.ageDistribution ? (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={Object.entries(drilldown.ageDistribution).map(([bracket, count]) => ({
                      bracket,
                      count
                    }))}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                    <XAxis dataKey="bracket" stroke="#94A3B8" fontSize={11} tickLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0F172A', borderColor: '#1E293B' }}
                      labelStyle={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}
                      itemStyle={{ color: '#6CD800', fontSize: 12 }}
                    />
                    <Bar dataKey="count" fill="#6CD800" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">No age distribution data available.</div>
            )}
          </div>

          {/* Retention Stats Card */}
          <div className="space-y-6">
            {/* Retention Overview */}
            <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Retention Indices</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-[#1E293B]">
                  <span className="text-xs text-gray-400 flex items-center"><Users size={14} className="mr-1.5" /> 7-Day Active</span>
                  <span className="text-sm font-bold text-white">{drilldown.retention.activeLast7Days} / {drilldown.retention.totalMembers}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#1E293B]">
                  <span className="text-xs text-gray-400 flex items-center"><Users size={14} className="mr-1.5" /> 30-Day Active</span>
                  <span className="text-sm font-bold text-white">{drilldown.retention.activeLast30Days} / {drilldown.retention.totalMembers}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#1E293B]">
                  <span className="text-xs text-gray-400 flex items-center"><TrendingUp size={14} className="mr-1.5" /> 7-Day Retention</span>
                  <span className="text-sm font-bold text-[#6CD800]">{drilldown.retention.retentionRate7}%</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-xs text-gray-400 flex items-center"><TrendingUp size={14} className="mr-1.5" /> 30-Day Retention</span>
                  <span className="text-sm font-bold text-[#6CD800]">{drilldown.retention.retentionRate30}%</span>
                </div>
              </div>
            </div>

            {/* Gamification Stats */}
            <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Challenge Streaking</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-[#1E293B]">
                  <span className="text-xs text-gray-400 flex items-center"><Award size={14} className="mr-1.5" /> Average Streak Length</span>
                  <span className="text-sm font-bold text-white">{drilldown.challengeStats.activeStreaks} days</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-xs text-gray-400 flex items-center"><Calendar size={14} className="mr-1.5" /> Total Scans (Historical)</span>
                  <span className="text-sm font-bold text-white">{drilldown.challengeStats.totalCheckins}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-400 py-12">No data available.</div>
      )}
    </div>
  );
};

const formatChartDate = (dateStr, idx, total) => {
  if (!dateStr) return '';
  if (!dateStr.includes('-')) return dateStr; // E.g., fallback Mon/Tue

  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const formatted = `${parts[1]}-${parts[2]}`; // MM-dd
    if (total > 10) {
      return idx % 5 === 0 ? formatted : '';
    }
    return formatted;
  }
  return dateStr;
};

export default Analytics;
